import React, { useState, useEffect } from "react";
import {
  Box,
  TextField,
  IconButton,
  Typography,
  Button,
  Snackbar,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import useAuthenticatedFetch from "../hooks/useAuthenticatedFetch";
import { useTranslation } from "react-i18next";
import { useAuth } from "../AuthContext";
import LocalizedTextField from "../components/common/LocalizedTextField";

const RoomEditor = ({ onIsDirtyChange }) => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [rooms, setRooms] = useState([]);
  const [successMessage, setSuccessMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [errors, setErrors] = useState([]); // Pour gérer les erreurs par ligne
  const fetchWithAuth = useAuthenticatedFetch();
  const [confirmDelete, setConfirmDelete] = useState(false); // État pour gérer la popup de confirmation
  const [roomToDelete, setRoomToDelete] = useState(null); // Salle sélectionnée pour suppression
  const [isDirty, setIsDirty] = useState(false);

  useEffect(() => {
    if (onIsDirtyChange) {
      onIsDirtyChange(isDirty); // Notifier le parent chaque fois que isDirty change
    }
  }, [isDirty, onIsDirtyChange]);
  
  useEffect(() => {
    const fetchData = async () => {
        // Charger les salles
        fetchWithAuth(`${process.env.REACT_APP_BASE_URL}/room/getRooms?communityId=${user.communityId}`)
        .then((roomsResponse) => {
            if (!roomsResponse.ok) {
            throw new Error(t("schedule.errors.loadRooms"));
            }
            return roomsResponse.json();
        })
        .then((roomsData) => {
            setRooms(roomsData);
            setErrors(new Array(roomsData.length).fill(false)); // Initialiser sans erreurs
        })
        .catch((error) => {
            console.error(error.message);
        });
    };

    fetchData();
  }, [user]);

  const handleAddRow = () => {
    setRooms([...rooms, { roomId: null, roomName: "", roomFloor: ""}]);
    setErrors([...errors, false]); // Ajouter une entrée d'erreur
    setIsDirty(true);
  };

  const handleUpdateRow = (index, field, value) => {
    const updatedRooms = [...rooms];
    updatedRooms[index][field] = value;
    setRooms(updatedRooms);
    // Réinitialiser l'erreur si le champ est rempli
    if (field === "roomName" && value.trim() !== "") {
      const updatedErrors = [...errors];
      updatedErrors[index] = false;
      setErrors(updatedErrors);
    }
    setIsDirty(true);
  };

  const handleOpenDeleteDialog = (room) => {
    setRoomToDelete(room);
    setConfirmDelete(true);
  };

  const handleCloseDeleteDialog = () => {
    setConfirmDelete(false);
    setRoomToDelete(null);
  };

  const handleConfirmDelete = async () => {
    if (!roomToDelete) return;

    try {
      await fetchWithAuth(`${process.env.REACT_APP_BASE_URL}/room/deleteRoom`, {
        method: "DELETE",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ communityId: user.communityId, roomId: roomToDelete.roomId }),
      });

      setRooms(rooms.filter((room) => room.roomId !== roomToDelete.roomId));
      setSuccessMessage(t("rooms.success.delete"));
    } catch (error) {
      console.error(error.message);
      setErrorMessage(t("rooms.errors.deleteError"));
    } finally {
        // Fermer le dialogue
        handleCloseDeleteDialog();
      }
  };
  const validateFields = () => {
    const updatedErrors = rooms.map((room) => !room.roomName.trim());
    setErrors(updatedErrors);
    return !updatedErrors.includes(true); // Retourne false s'il y a des erreurs
  };

  const handleSave = async () => {
    if (!validateFields()) {
      setErrorMessage(t("common.validationError")); // Message d'erreur global
      return;
    }

    try {
      await fetchWithAuth(`${process.env.REACT_APP_BASE_URL}/room/saveRooms`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ communityId: user.communityId, rows: rooms }),
      });
      setIsDirty(false);
      setSuccessMessage(t("rooms.success.save"));
    } catch (error) {
      console.error(error.message);
      setErrorMessage(t("rooms.errors.saveError"));
    }
  };

  return (
    <Box sx={{ mt: 1, width: "100%" }}>
      <Typography variant="h4" gutterBottom>
        {t("rooms.title")}
      </Typography>
      <Box sx={{ display: "flex", flexDirection: "column", width: "100%" }}>
        {rooms.map((room, index) => (
          <Box
            key={index}
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 2,
              borderBottom: "1px solid #ccc",
              paddingBottom: 1,
              marginBottom: 2,
            }}
          >
            <LocalizedTextField
              label={t("rooms.name")}
              value={room.roomName}
              onChange={(e) => handleUpdateRow(index, "roomName", e.target.value)}
              fullWidth
              error={errors[index]}
              helperText={errors[index] ? t("common.required") : ""}
            />
            <LocalizedTextField
              label={t("rooms.floor")}
              value={room.roomFloor}
              onChange={(e) => handleUpdateRow(index, "roomFloor", e.target.value)}
              fullWidth
            />
            <IconButton onClick={() => handleOpenDeleteDialog(room)}
              color="error">
              <DeleteIcon />
            </IconButton>
          </Box>
        ))}

        <Box sx={{ display: "flex", justifyContent: "center", marginTop: 2 }}>
          <IconButton onClick={handleAddRow} color="primary">
            <AddIcon />
          </IconButton>
        </Box>
      </Box>

      <Box sx={{ mt: 2 }}>
        <Button onClick={handleSave} variant="contained" color="primary">
          {t("rooms.save")}
        </Button>
      </Box>

      <Snackbar
        open={!!successMessage}
        autoHideDuration={3000}
        onClose={() => setSuccessMessage("")}
        message={successMessage}
      />
      <Snackbar
        open={!!errorMessage}
        autoHideDuration={3000}
        onClose={() => setErrorMessage("")}
        message={errorMessage}
      />
       {/* Dialog de confirmation */}
       <Dialog
        open={confirmDelete}
        onClose={handleCloseDeleteDialog}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">
          {t("rooms.delete")}
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            {t( "rooms.confirmDelete")}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDeleteDialog} color="primary">
            {t("rooms.cancel")}
          </Button>
          <Button
            onClick={handleConfirmDelete}
            color="error"
            autoFocus
          >
            {t("rooms.delete")}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default RoomEditor;